<script lang="ts" setup>
import { BrComponent, BrPage } from '@bloomreach/vue-sdk'
import { inject, provide, ref } from 'vue'
import type { IBrxmProps } from '../../ts/interfaces/IBrxmProps'
import { useBrxm } from '../../composables/useBrxm'
import { useBrxmData } from '../../composables/useBrxmData'
import { useCTAButton, useCTALink } from '../CTABlock/ts/useCTAButtons'
import { MAIN_PAGE } from '../../consts/injectProvideKeys'
import { ECTABlockButtonAlignment } from '../CTABlock/ts/types'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'
/**
 * SDX
 */
const props = defineProps<IBrxmProps>()

const injectedPage = inject(MAIN_PAGE)
if (!injectedPage) {
    provide(MAIN_PAGE, props.page)
}

const { mapping, configuration } = await useBrxm()

const data = useBrxmData(props, 'SECTION_WRAPPER')
const { background, darkmode, additionalText, components, button1, button2, link } = data?.value

const button1Config = useCTAButton(button1)
const button2Config = useCTAButton(button2)
const linkConfig = useCTALink(link)
const isTitleBlock = data?.value.title || data?.value.subtitle || data?.value.paragraph?.value || data?.value.badges
const sectionWrapperRef = ref<HTMLElement | null>(null)

useVisibilityTracking(sectionWrapperRef, 'SectionWrapper', 'SectionWrapper')
</script>

<template>
    <BackgroundBlock
        ref="sectionWrapperRef"
        class="bg-neutral-primary bg-no-repeat"
        :class="[background ? 'space-background py-xl md:py-2xl lg:py-3xl' : 'space-section']"
        :page="page"
        :background="background"
    >
        <div
            v-if="isTitleBlock"
            class="gap-md container mx-auto flex flex-col"
        >
            <div
                class="container-grid"
                :class="{ 'pt-base': !background }"
            >
                <TitleBlockPartsTitleBlockComponent
                    :heading-block-aligment="data?.alignment"
                    :heading-block-size="data?.size"
                    :heading="data?.title"
                    :seo-heading-size="data?.seoHeadingSize?.value"
                    :subheading="data?.subtitle"
                    :paragraph="data?.paragraph?.value"
                    :badges="data?.badges"
                    :dark="darkmode"
                />
            </div>
        </div>

        <SectionWrapperPartsComponentSlot
            v-if="components?.length"
            class="light"
            :class="{ 'mt-md md:mt-lg lg:mt-xl': isTitleBlock }"
        >
            <template
                v-for="(child, index) in components"
                :key="`component_${index}`"
            >
                <BrPage
                    :configuration="configuration"
                    :mapping="mapping"
                    :page="child"
                >
                    <template #default="{ component }">
                        <BrComponent :component="component" />
                    </template>
                </BrPage>
            </template>
        </SectionWrapperPartsComponentSlot>

        <div
            v-if="button1 || button2 || link || additionalText?.value"
            class="mt-md md:mt-lg lg:mt-xl"
        >
            <div class="gap-md container mx-auto flex flex-col">
                <div
                    v-if="additionalText?.value"
                    class="container-grid"
                >
                    <SectionWrapperPartsAdditionalText
                        :dark="darkmode"
                        :text="additionalText?.value"
                    />
                </div>

                <CTABlock
                    v-if="button1 || button2 || link"
                    :dark="darkmode"
                    inline
                    :cta-button1="button1Config"
                    :cta-button2="button2Config"
                    :cta-link="linkConfig"
                    :page="page"
                    :alignment="ECTABlockButtonAlignment.CENTER"
                    :alignment-desktop="ECTABlockButtonAlignment.CENTER"
                />
            </div>
        </div>
    </BackgroundBlock>
</template>
